import {
  Card,
  CardHeader,
  Field,
  Input,
} from "@/components/primitives";
import { Icon } from "@/components/Icon";
import { PageHeader } from "@/components/Layout";
import { getAuth, isAdmin } from "@/lib/auth";

const NOTIFICATIONS = [
  {
    name: "Slack · #it-status",
    detail: "Posts to channel on new incident + status change",
    icon: "webhook",
    on: true,
    sev: "all",
  },
  {
    name: "PagerDuty · IT On-Call",
    detail: "Pages on-call only for major-severity outages",
    icon: "bell",
    on: true,
    sev: "major",
  },
  {
    name: "Email · ops@example.com",
    detail: "Daily digest at 09:00 CST",
    icon: "globe",
    on: true,
    sev: "digest",
  },
  {
    name: "Webhook · internal incidents",
    detail: "Forward all events to an internal incident webhook",
    icon: "webhook",
    on: false,
    sev: "all",
  },
  {
    name: "SMS · on-call rotation",
    detail: "For major outages only · uses Twilio",
    icon: "bell",
    on: false,
    sev: "major",
  },
] as const;

export function Settings() {
  const auth = getAuth();
  return (
    <>
      <PageHeader
        crumbs={["Status Watch", "Settings"]}
        title="Settings"
        subtitle="Workspace settings"
      />
      <div
        style={{
          padding: "20px 32px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          maxWidth: 920,
        }}
      >
        <Card>
          <CardHeader title="Account" description="Signed in via Cognito" />
          <div
            style={{
              padding: 16,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            <Mini label="Email" value={auth?.email ?? "—"} />
            <Mini
              label="Role"
              value={isAdmin() ? "admin" : "viewer"}
              tone={isAdmin() ? "primary" : "muted"}
            />
            <Mini label="Groups" value={auth?.groups.join(", ") || "—"} />
            <Mini
              label="Session expires"
              value={
                auth?.expiresAt
                  ? new Date(auth.expiresAt).toLocaleString()
                  : "—"
              }
            />
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Notifications"
            description="Where incidents are delivered."
          />
          <div style={{ padding: 4 }}>
            {NOTIFICATIONS.map((row, i) => (
              <div
                key={row.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 16px",
                  borderTop: i ? "1px solid var(--border)" : "none",
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "var(--muted)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--muted-foreground)",
                  }}
                >
                  <Icon name={row.icon} size={15} />
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {row.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--muted-foreground)",
                      marginTop: 2,
                    }}
                  >
                    {row.detail}
                  </div>
                </div>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: 22,
                    padding: "0 10px",
                    borderRadius: 5,
                    fontSize: 11,
                    fontWeight: 500,
                    background: "var(--muted)",
                    color: "var(--muted-foreground)",
                  }}
                >
                  {row.sev === "all"
                    ? "all severities"
                    : row.sev === "major"
                      ? "major only"
                      : row.sev}
                </span>
                <Toggle on={row.on} />
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Polling"
            description="Defaults applied when adding an RSS-based service"
          />
          <div
            style={{
              padding: 16,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            <Field
              label="Default RSS interval"
              hint="Minimum 30s; providers vary"
            >
              <Input value="60s" readOnly mono />
            </Field>
            <Field
              label="Backoff on error"
              hint="Doubled each consecutive failure, up to 15m"
            >
              <Input value="exponential · max 15m" readOnly />
            </Field>
            <Field
              label="Retain incident history"
              hint="How long resolved incidents are kept"
            >
              <Input value="365 days" readOnly />
            </Field>
            <Field
              label="Timezone"
              hint="For digest delivery and dashboard timestamps"
            >
              <Input value="UTC · displayed in viewer's locale" readOnly />
            </Field>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Webhook security"
            description="HMAC signing applied to all inbound endpoints"
          />
          <div style={{ padding: 16 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 14,
              }}
            >
              <Mini label="Algorithm" value="HMAC-SHA256" />
              <Mini label="Header" value="X-StatusWatch-Signature" />
              <Mini label="Replay window" value="5 minutes" />
            </div>
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: "var(--muted)",
                borderRadius: 8,
                fontSize: 12,
                color: "var(--muted-foreground)",
                lineHeight: 1.5,
              }}
            >
              Each inbound event is rejected if the timestamp is older than 5
              minutes or if the signature in
              <code
                style={{
                  background: "var(--card)",
                  padding: "0 4px",
                  margin: "0 4px",
                  borderRadius: 3,
                }}
              >
                X-StatusWatch-Signature
              </code>
              does not match a computed HMAC over the request body using the
              service's signing secret.
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}

function Mini({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "primary" | "muted";
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--muted-foreground)",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          marginTop: 4,
          color: tone === "primary" ? "var(--primary)" : "var(--foreground)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Toggle({ on }: { on: boolean }) {
  return (
    <span
      style={{
        width: 32,
        height: 18,
        borderRadius: 999,
        position: "relative",
        background: on ? "var(--primary)" : "var(--muted)",
        transition: "background 150ms ease",
        cursor: "pointer",
        display: "inline-block",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: on ? 16 : 2,
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 150ms ease",
          boxShadow: "0 1px 2px rgba(0,0,0,.2)",
        }}
      />
    </span>
  );
}
